import type {
  BaseRecord,
  CrudFilters,
  DataProvider,
  GetListParams,
  GetOneParams,
} from '@refinedev/core'

import { settlementGetSettlementDetails, settlementListSettlements } from '@repo/sdk'

import { getStoredUser } from '../lib/auth-storage'
import { i18n } from '../shared/i18n'

import { handleSdkError } from './sdkErrorUtils'
import { getApiUrl } from '../lib/runtime-env'

type SettlementStatusFilter = 'ALL' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'BLOCKED'

const VALID_STATUS_FILTERS: ReadonlySet<string> = new Set<SettlementStatusFilter>([
  'ALL',
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'BLOCKED',
])

function isSettlementStatusFilter(value: unknown): value is SettlementStatusFilter {
  return typeof value === 'string' && VALID_STATUS_FILTERS.has(value)
}

function extractStatusFilter(filters?: CrudFilters): SettlementStatusFilter | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if (
      'field' in filter &&
      filter.field === 'status' &&
      'value' in filter &&
      isSettlementStatusFilter(filter.value)
    ) {
      return filter.value
    }
  }
  return undefined
}

function extractDateFilter(filters: CrudFilters | undefined, field: string): string | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if ('field' in filter && filter.field === field && 'value' in filter) {
      return String(filter.value)
    }
  }
  return undefined
}

function notImplemented(): never {
  throw new Error(i18n.t('common.errors.notImplemented'))
}

export const settlementHistoryDataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>(params: GetListParams) => {
    try {
      const dealerId = getStoredUser()?.userId
      // Falling back to '' produced a confusing 400 from the API — fail with a message
      // the dealer can act on instead.
      if (!dealerId) throw new Error(i18n.t('common.errors.dealerIdRequired'))
      const page = params.pagination?.currentPage ?? 1
      const pageSize = params.pagination?.pageSize ?? 20
      const statusFilter = extractStatusFilter(params.filters)
      const dateFrom = extractDateFilter(params.filters, 'dateFrom')
      const dateTo = extractDateFilter(params.filters, 'dateTo')

      const response = await settlementListSettlements({
        query: { dealerId, page, pageSize, statusFilter, dateFrom, dateTo },
      })

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Refine DataProvider generic requires TData cast
        data: (response.data?.data ?? []) as unknown as TData[],
        total: response.data?.meta.totalItems ?? 0,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(params: GetOneParams) => {
    try {
      const settlementId = String(params.id)
      const response = await settlementGetSettlementDetails({
        path: { settlementId },
      })

      if (!response.data) {
        throw new Error(i18n.t('common.errors.detailNotFound'))
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Refine DataProvider generic requires TData cast
        data: response.data as unknown as TData,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },
  create: notImplemented,
  update: notImplemented,
  deleteOne: notImplemented,
  getApiUrl,
}
